import { localeFor } from "./languages";
import type { Attachment } from "../types";
import { getLocalizedError, translations, type Language } from "../i18n";
import { classifyFile, createLocalAttachment, MAX_DOCUMENT_BYTES, MAX_IMAGE_BYTES } from "./attachments";
import { fetchWithDeadline, readResponseJson } from "./request-errors";
import { getClientId } from "./client-id";

export async function prepareAttachment(file: File, options: { language: Language; supportsImages: boolean }): Promise<Attachment> {
  const t = translations[options.language];
  const kind = classifyFile(file);
  if (kind === "unsupported") throw new Error(t.unsupportedAttachment);
  if (kind === "image" && !options.supportsImages) throw new Error(t.imageNeedsVision);
  if (file.size > (kind === "image" ? MAX_IMAGE_BYTES : MAX_DOCUMENT_BYTES)) throw new Error(kind === "image" ? t.imageTooLarge : t.documentTooLarge);
  const local = await createLocalAttachment(file);
  if (local) return local;
  const form = new FormData();
  form.set("file", file);
  const response = await fetchWithDeadline("/api/attachments/convert", {
    method: "POST", headers: { "x-neurondeck-client": getClientId(), "accept-language": localeFor(options.language) }, body: form,
  });
  const data = await readResponseJson(response) as { name?: string; mimeType?: string; size?: number; text?: string; tokens?: number; truncated?: boolean; error?: { code?: string; message?: string } } | null;
  if (!response.ok || !data?.text) throw new Error(getLocalizedError(options.language, data?.error?.code, data?.error?.message || t.conversionFailed));
  return { id: crypto.randomUUID(), kind: "file", name: data.name || file.name, mimeType: data.mimeType || file.type || "application/octet-stream", size: data.size ?? file.size, text: data.text, tokens: data.tokens, truncated: data.truncated };
}
